<script>
import countries from '@/constants/countries';
import AdditionalVisitorsService from '@/services/AdditionalVisitorsService';
import { extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import VisitorService from '@/services/VisitorService';
import { ERROR_CODES } from '@/util/errorCodes';

extend('required', {
  ...required,
  message: 'This field is required',
});

export default {
  name: 'CreateAdditionalVisitor',
  data() {
    return {
      loading: false,
      searchingVisitors: false,
      visitorsWithContactInPrison: [],
      visitorLastName: null,
      selectedVisitor: null,
      additionalVisitor: {
        address: {},
      },
    };
  },
  methods: {
    visitors() {
      return this.visitorsWithContactInPrison.map(v => ({
        value: v,
        text: `${v.firstname} ${v.lastname}`,
      }));
    },
    countries() {
      return countries.map(c => ({ value: c.value, text: c.text }))
    },
    async getVisitorsWithContactInPrison() {
      try {
        this.searchingVisitors = true;
        this.visitorsWithContactInPrison = [];
        this.selectedVisitor = null;

        const response = await VisitorService.getVisitorsWithContactInPrisonPaginated({
          lastname: this.visitorLastName,
        });

        this.visitorsWithContactInPrison = response?.data?.data || [];

        if (this.visitorsWithContactInPrison.length) {
          this.$swal(
            'Great news!',
            `${this.visitorsWithContactInPrison.length} ${this.visitorsWithContactInPrison.length <= 1 ? 'visitor has' : 'visitors have'} been found. Please select one from the drop down menu below.`,
            'success',
          );
        } else {
          this.$swal(
            'No visitors found',
            'Please check the last name entered and try again.',
            'warning',
          );
        }
      } catch (e) {
        console.error('Error getting visitors with contact in prison: ', e);
      } finally {
        this.searchingVisitors = false;
      }
    },
    useVisitorAddress() {
      this.additionalVisitor.address = this.selectedVisitor.address;
    },
    async submitDetails() {
      try {
        if (!await this.$refs.createAdditionalVisitor.validate()) {
          return;
        }
        this.loading = true;

        const visitorUserId = this.selectedVisitor.user_id;
        await AdditionalVisitorsService.createOnDemandAdditionalVisitor(visitorUserId, this.additionalVisitor)

        this.$toast.success('Additional Visitor successfully created');
        this.$router.push({ name: 'visitor-profile', params: { id: visitorUserId } })
      } catch (e) {
        console.error('Error submitting details: ', e);
        const errorCode = e?.response?.data?.error ?? ''
        const errorTitle = 'Error';
        let errorMessage = 'An unexpected error occurred. Please refresh and try again.';

        if (errorCode === ERROR_CODES.VISITOR.NOT_VERIFIED) {
          errorMessage = 'This visitor is not verified';
        } else if (ERROR_CODES.ENTITY.DATE_IN_FUTURE) {
          errorMessage = 'The date of birth is in the future'
        }

        this.$swal(errorTitle, errorMessage, 'warning')
      } finally {
        this.loading = false;
      }
    },
    selectVisitor() {
      this.additionalVisitor.address = {};
    },
  },
}
</script>

<template>
  <div>
    <h2 cy="page-title" class="font-weight-bolder">
      Create Additional Visitor
    </h2>
    <p>
      Use the form below to add additional visitors to the visitor's account
    </p>
    <validation-observer ref="createAdditionalVisitor">
      <b-form @submit.prevent @keydown.enter.prevent="submitDetails">
        <b-row>
          <b-col cols="12">
            <b-card title="Visitor details">
              <div class="form-group">
                <div class="control-label">
                  Visitor last name
                </div>
                <div class="input-group">
                  <input
                    id="visitor-last-name"
                    v-model="visitorLastName"
                    class="form-control"
                    placeholder="Enter the visitor's last name"
                    type="text"
                  >
                  <div class="input-group-append">
                    <b-btn
                      variant="primary"
                      :disabled="searchingVisitors"
                      @click.stop.prevent="getVisitorsWithContactInPrison"
                    >
                      {{ searchingVisitors ? 'Loading...' : 'Search' }}
                    </b-btn>
                  </div>
                </div>
              </div>
              <b-form-group label="Visitor*">
                <validation-provider
                  key="visitor"
                  #default="{ errors }"
                  v-model="selectedVisitor"
                  name="selected-visitor"
                  rules="required"
                >
                  <b-form-select
                    id="selected-visitor"
                    v-model="selectedVisitor"
                    :disabled="!visitors().length"
                    :state="errors.length > 0 ? false:null"
                    :options="visitors()"
                    placeholder="Select visitor"
                    type="select"
                    @change="selectVisitor"
                  />
                </validation-provider>
              </b-form-group>
            </b-card>
          </b-col>
        </b-row>
      </b-form>

      <b-form>
        <b-row>
          <b-col cols="12">
            <b-card title="Personal details">
              <b-form-group label="First name">
                <validation-provider
                  key="av-first-name"
                  #default="{ errors }"
                  name="av-first-name"
                  rules="required"
                >
                  <b-form-input
                    id="av-first-name"
                    v-model="additionalVisitor.firstname"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their first name"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Last name">
                <validation-provider
                  key="av-last-name"
                  #default="{ errors }"
                  name="av-last-name"
                  rules="required"
                >
                  <b-form-input
                    id="av-last-name"
                    v-model="additionalVisitor.lastname"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their last name"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Date of birth">
                <validation-provider
                  key="av-dob"
                  #default="{ errors }"
                  name="av-date-of-birth"
                  rules="required"
                >
                  <b-form-datepicker
                    id="av-dob"
                    v-model="additionalVisitor.dob"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Select their date of birth"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
            </b-card>
            <b-card title="Address Info">
              <b-btn class="mb-2" :disabled="!selectedVisitor" @click.stop.prevent="useVisitorAddress">
                Use visitor address
              </b-btn>
              <b-form-group label="Address line 1*">
                <validation-provider
                  key="av-address-1"
                  #default="{ errors }"
                  name="av-address-line-1"
                  rules="required"
                >
                  <b-form-input
                    id="av-address-1"
                    v-model="additionalVisitor.address.address1"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the first line of their address"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Address line 2*">
                <validation-provider
                  key="av-address-2"
                  #default="{ errors }"
                  name="av-address-line-2"
                  rules="required"
                >
                  <b-form-input
                    id="av-address-2"
                    v-model="additionalVisitor.address.address2"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the second line of their address"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Address line 3">
                <validation-provider
                  key="av-address-3"
                  #default="{ errors }"
                  name="av-address-line-3"
                >
                  <b-form-input
                    id="av-address-3"
                    v-model="additionalVisitor.address.address3"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the third line of their address"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Town/City*">
                <validation-provider
                  key="av-address-city"
                  #default="{ errors }"
                  name="av-town-city"
                  rules="required"
                >
                  <b-form-input
                    id="av-address-city"
                    v-model="additionalVisitor.address.city_town"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their town/city"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="County/Region*">
                <validation-provider
                  key="av-address-region"
                  #default="{ errors }"
                  name="av-county"
                  rules="required"
                >
                  <b-form-input
                    id="av-address-region"
                    v-model="additionalVisitor.address.region"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their county"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Zip/Postcode*">
                <validation-provider
                  key="av-address-postcode"
                  #default="{ errors }"
                  name="av-postcode"
                  rules="required"
                >
                  <b-form-input
                    id="av-address-postcode"
                    v-model="additionalVisitor.address.zip_postal_code"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their postcode"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Country*">
                <validation-provider
                  key="av-address-country"
                  #default="{ errors }"
                  v-model="additionalVisitor.address.county"
                  name="av-country"
                  rules="required"
                >
                  <b-form-select
                    id="av-address-country"
                    v-model="additionalVisitor.address.country"
                    :state="errors.length > 0 ? false:null"
                    :options="countries()"
                    placeholder="Select country"
                    type="select"
                  />
                </validation-provider>
              </b-form-group>
            </b-card>
            <div class="d-flex">
              <div class="ui-spacer" />
              <b-btn
                variant="primary"
                :disabled="loading"
                @click.stop.prevent="submitDetails"
              >
                {{ loading ? 'Loading...' : 'Create' }}
              </b-btn>
            </div>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </div>
</template>
